// connection.js — open a WebSocket to HA and expose helpers shaped
// like HA's frontend `hass` object so existing components can keep
// reading `hass.states`, calling `hass.callService`, etc.

import {
  createConnection,
  subscribeEntities,
  subscribeConfig,
  callService,
} from 'home-assistant-js-websocket';

// Open the WS using a previously-acquired Auth object. Connection
// auto-reconnects on disconnect — the library handles backoff and
// re-issues subscriptions on reconnect.
export async function openConnection(auth) {
  return createConnection({ auth });
}

// Build a `hass`-shaped facade around the live connection. The shape
// is the *minimal* subset our panel uses today:
//   - hass.states: { [entity_id]: HassEntity }
//   - hass.config: HassConfig
//   - hass.user: HassUser (optional, fetched on demand)
//   - hass.callService(domain, service, data?, target?)
//   - hass.callApi(method, path, body?, headers?)
//   - hass.callWS(message)
//   - hass.auth: the Auth object (for accessToken access)
//   - hass.connection: the raw Connection (escape hatch)
//
// `onUpdate` is invoked whenever entity state changes — the React
// hook layer maps it to setState/forceRender.
// `onServiceError` is invoked when a service call rejects (unless the
// call site opted out via notifyOnError=false) — the UI layer maps it
// to a visible toast, mirroring HA frontend behaviour.
export function buildHass(auth, connection, { onUpdate, onServiceError }) {
  const facade = {
    auth,
    connection,
    states: {},
    config: null,
    user: null,
    // Mirror HA's frontend signature so existing call sites work unchanged:
    //   callService(domain, service, serviceData?, target?, notifyOnError?, returnResponse?)
    //
    // `notifyOnError` matches HA-frontend semantics: default true → a
    // rejected call surfaces via onServiceError; pass false to handle
    // the error yourself. `returnResponse=true` is required for
    // "response services" like sonos.get_queue — without it HA returns
    // service_validation_error.
    callService(domain, service, serviceData, target, notifyOnError = true, returnResponse) {
      const call = callService(connection, domain, service, serviceData, target, returnResponse);
      if (notifyOnError !== false) {
        // Attach a handler on the returned promise so fire-and-forget
        // call sites (most of the panel) get a toast instead of a
        // silent unhandled rejection. Call sites that await still see
        // the rejection themselves.
        call.catch((err) => onServiceError?.(err, { domain, service }));
      }
      return call;
    },
    // HA REST API: same shape as HA frontend's hass.callApi.
    // method, path are required; body is optional; returns parsed JSON.
    async callApi(method, path, body, headers = {}) {
      const url = (auth.data.hassUrl || '') + (path.startsWith('/') ? path : `/${path}`);
      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${auth.accessToken}`,
          'Content-Type': 'application/json',
          ...headers,
        },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`callApi ${method} ${path} → ${res.status}: ${text}`);
      }
      const ct = res.headers.get('content-type') || '';
      return ct.includes('application/json') ? res.json() : res.text();
    },
    // Raw WS message (matches HA frontend's hass.callWS).
    callWS(message) {
      return connection.sendMessagePromise(message);
    },
  };

  // Stream entity state into facade.states. The library debounces
  // bursts and delivers a fresh map on every transition. Replace the
  // entire `states` reference so React identity-based memos invalidate.
  // `reason` lets the consumer filter entity churn without also
  // filtering config updates, which are rare and must always land.
  const unsubEntities = subscribeEntities(connection, (states) => {
    facade.states = states;
    onUpdate?.(facade, 'entities');
  });

  const unsubConfig = subscribeConfig(connection, (config) => {
    facade.config = config;
    onUpdate?.(facade, 'config');
  });

  return {
    hass: facade,
    unsubscribe() {
      unsubEntities();
      unsubConfig();
    },
  };
}
